import { activeOrdersAtom } from "@/atoms/active-orders"
import { CHROME_Z_INDEX } from "@/contexts/ModalContext"
import { OrderIcon } from "@/icons"
import { cn } from "@/lib/cn"
import { getCurrentStatus, getOrderProgressSummary } from "@/lib/order-status"
import { useAtomValue } from "jotai"
import { useEffect, useRef } from "react"
import { matchPath, useLocation, useNavigate } from "react-router-dom"
import OrderProgressBar from "./OrderProgressBar"
import { Badge, IconBadge, InteractiveCard, PageContainer, Surface, Text } from "@/components/ui"

/**
 * Routes where the bar would be redundant or visually competing.
 * - `/profile/orders/:id` already shows the full status card.
 * - Authentication routes run with no header/profile.
 */
const HIDDEN_ROUTE_PATTERNS: readonly string[] = [
  "/profile/orders",
  "/profile/orders/:id",
  "/profile/auth",
  "/profile/auth/*",
  "/cart/checkout",
  "/cart/checkout/*",
  "/cart/order-confirmed/:orderId",
]

function shouldHideOnPath(pathname: string): boolean {
  return HIDDEN_ROUTE_PATTERNS.some((pattern) => matchPath(pattern, pathname) !== null)
}

/**
 * Global banner rendered below the hamburger header that surfaces
 * the customer's in-progress order. Tapping the bar navigates to the
 * order detail (or to the orders list when multiple are in progress).
 *
 * Layout notes:
 * - Fixed at `top-header` (right below the hamburger). O z-index vem de
 *   `CHROME_Z_INDEX.activeOrderBar`, o degrau mais baixo da faixa de chrome:
 *   abaixo do menu e de qualquer modal. Ver docs/Z-INDEX-LAYERS.md.
 * - Renders a same-height spacer after itself so the page content below
 *   is pushed down and never ends up hidden under the bar.
 */
type ActiveOrderBarProps = {
  hideMenuAbove?: boolean
  /**
   * When true, render in normal document flow (no fixed positioning,
   * no spacer). For pages that want the bar to sit below their own
   * sticky header instead of pinned to the viewport top.
   */
  inline?: boolean
  /**
   * When true, ignore the global hidden-route check. Use it together
   * with `inline` so the route can hide the global instance and render
   * its own copy in-flow.
   */
  forceVisible?: boolean
}

export function ActiveOrderBar({
  hideMenuAbove = false,
  inline = false,
  forceVisible = false,
}: ActiveOrderBarProps = {}) {
  const activeOrders = useAtomValue(activeOrdersAtom)
  const location = useLocation()
  const navigate = useNavigate()
  const lastLoggedOrderIdRef = useRef<string | null>(null)

  const primary = activeOrders[0]
  const hidden =
    activeOrders.length === 0 ||
    (!forceVisible && shouldHideOnPath(location.pathname))

  useEffect(() => {
    if (hidden || !primary) {
      lastLoggedOrderIdRef.current = null
      return
    }
    if (lastLoggedOrderIdRef.current === primary.id) return
    lastLoggedOrderIdRef.current = primary.id
    console.info("[telemetry] active_order_bar_shown", {
      orderId: primary.id,
      status: getCurrentStatus(primary),
      totalActive: activeOrders.length,
    })
  }, [hidden, primary, activeOrders.length])

  useEffect(() => {
    if (hidden || !primary || inline) return
    document.documentElement.style.setProperty("--active-order-bar-height", "4.5rem")
    return () => {
      document.documentElement.style.removeProperty("--active-order-bar-height")
    }
  }, [hidden, primary, inline])

  if (hidden || !primary) return null

  const extraCount = activeOrders.length - 1
  const { label, progress, progressTone, arrivalLabel } = getOrderProgressSummary(primary)
  const arrivalPrefix =
    primary.type === "DELIVERY" || primary.type === "INDOOR" ? "Entrega" : "Retirada"

  function handleClick() {
    console.info("[telemetry] active_order_bar_clicked", {
      orderId: primary.id,
      totalActive: activeOrders.length,
    })
    if (activeOrders.length > 1) {
      navigate("/profile/orders")
    } else {
      navigate(`/profile/orders/${primary.id}`)
    }
  }

  const barContent = (
    <PageContainer className="max-w-shell py-2.5">
      <InteractiveCard padding="compact" accent="primary" onClick={handleClick} className="w-full">
        <div className="flex items-center gap-3">
          <IconBadge icon={OrderIcon} tone="primary" variant="solid" size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <Text size="sm" weight="bold" tone="default" truncate>
                {label}
              </Text>
              {extraCount > 0 ? (
                <Badge size="xs" tone="neutral" variant="outline">
                  +{extraCount} em andamento
                </Badge>
              ) : (
                <Text as="span" size="xs" weight="semibold" tone="muted" className="shrink-0">
                  {arrivalPrefix} {arrivalLabel}
                </Text>
              )}
            </div>
            <OrderProgressBar progress={progress} progressTone={progressTone} className="mt-2" />
          </div>
        </div>
      </InteractiveCard>
    </PageContainer>
  )

  if (inline) {
    return (
      <Surface surface="card" role="status" aria-live="polite">
        {barContent}
      </Surface>
    )
  }

  return (
    <>
      <Surface
        surface="page"
        role="status"
        aria-live="polite"
        style={{ zIndex: CHROME_Z_INDEX.activeOrderBar }}
        className={cn(
          "fixed left-0 right-0",
          hideMenuAbove ? "top-0" : "top-header",
        )}
      >
        {barContent}
      </Surface>

      <div aria-hidden className="pt-header pb-2" />
    </>
  )
}

export default ActiveOrderBar
